import { glMatrix } from 'gl-matrix'

import type {
  GridType,
  IWebGlRender,
  MinMax,
  Nullable,
  PaletteBody,
  Property,
  Range2D,
  ScaleType,
  VertexAttribute,
} from '../../../../types'
import {
  bindAttributes,
  bindIndexBuffer,
  createAttribute,
  createIndexBufferUint16,
  deleteAttributes,
  getGridCoordinatesToProjectionMatrix,
  getOrCreateProgram,
  getWebGlContext,
  unbindAttributes,
  updateAttributeBuffer,
} from '../../../../utils'
import { booleanToNumber, createPaletteTexture, createTextureBuffer } from './shaderHelpers'
import { fragmentShaderSource, vertexShaderSource } from './shaders'

type Attributes = 'aPosition' | 'aTextureCoord'

export class GridRender implements IWebGlRender, Disposable {
  private canvas: HTMLCanvasElement
  private program: WebGLProgram
  private renderingContext: WebGL2RenderingContext

  private textureBuffer: Nullable<WebGLTexture>
  private paletteTextureBuffer: Nullable<WebGLTexture>

  private attributes: Record<Attributes, VertexAttribute>

  private indexBuffer: Nullable<WebGLBuffer>

  private projectionLocation: Nullable<WebGLUniformLocation>
  private imageUniform: Nullable<WebGLUniformLocation>
  private nullValueUniform: Nullable<WebGLUniformLocation>
  private rangeMinUniform: Nullable<WebGLUniformLocation>
  private rangeMaxUniform: Nullable<WebGLUniformLocation>
  private paletteTextureUniform: Nullable<WebGLUniformLocation>
  private baseLineUniform: Nullable<WebGLUniformLocation>
  private isolineStepUniform: Nullable<WebGLUniformLocation>
  private majorIsolineUniform: Nullable<WebGLUniformLocation>
  private majorIsolineWidthUniform: Nullable<WebGLUniformLocation>
  private minorIsolineWidthUniform: Nullable<WebGLUniformLocation>
  private fillModeUniform: Nullable<WebGLUniformLocation>
  private showIsolineUniform: Nullable<WebGLUniformLocation>

  private imageWidth: number
  private imageHeight: number
  private nullValue: number
  private coordinates: Range2D
  private rotate: number
  private range: MinMax
  private baseline: number
  private isolineStep: number
  private majorIsoline: number
  private majorIsolineWidth: number
  private minorIsolineWidth: number
  private zoom: ScaleType
  private showIsolines: 0 | 1

  private isDisposed: boolean

  constructor(
    canvas: HTMLCanvasElement,
    programs: Property<Record<string, WebGLProgram>>,
    zoom: ScaleType,
    coordinates: Range2D,
    headerNullValue: number,
    rotate: number,
    imageHeight: number,
    imageBody: GridType['body'],
    baseline: number,
    isolineStep: number,
    majorIsoline: number,
    majorIsolineWidth: number,
    minorIsolineWidth: number,
    [rangeMin, rangeMax]: MinMax,
    palette: PaletteBody,
    showIsolines: boolean = true,
  ) {
    this.canvas = canvas
    this.zoom = zoom
    this.coordinates = coordinates
    this.rotate = rotate
    this.imageWidth = Math.ceil(imageBody.length / imageHeight)
    this.imageHeight = imageHeight
    this.nullValue = headerNullValue
    this.baseline = baseline
    this.isolineStep = isolineStep
    this.majorIsoline = majorIsoline
    this.majorIsolineWidth = majorIsolineWidth
    this.minorIsolineWidth = minorIsolineWidth
    this.range = [rangeMin, rangeMax]
    this.showIsolines = booleanToNumber(showIsolines)

    this.textureBuffer = null
    this.paletteTextureBuffer = null

    const gl = getWebGlContext(canvas)
    this.renderingContext = gl

    const program = getOrCreateProgram(programs, gl, vertexShaderSource, fragmentShaderSource)
    this.program = program

    this.attributes = {
      aPosition: createAttribute(gl, program, 'aPosition', 2, gl.FLOAT),
      aTextureCoord: createAttribute(gl, program, 'aTextureCoord', 2, gl.FLOAT),
    }

    this.indexBuffer = null

    this.projectionLocation = gl.getUniformLocation(program, 'uProjection')
    this.imageUniform = gl.getUniformLocation(program, 'uImage')
    this.paletteTextureUniform = gl.getUniformLocation(program, 'uPaletteTexture')
    this.nullValueUniform = gl.getUniformLocation(program, 'uNullValue')
    this.rangeMinUniform = gl.getUniformLocation(program, 'uRangeMin')
    this.rangeMaxUniform = gl.getUniformLocation(program, 'uRangeMax')
    this.baseLineUniform = gl.getUniformLocation(program, 'uBaseline')
    this.isolineStepUniform = gl.getUniformLocation(program, 'uIsolineStep')
    this.majorIsolineUniform = gl.getUniformLocation(program, 'uMajorIsolineFrequency')
    this.majorIsolineWidthUniform = gl.getUniformLocation(program, 'uMajorIsolineWidth')
    this.minorIsolineWidthUniform = gl.getUniformLocation(program, 'uMinorIsolineWidth')
    this.fillModeUniform = gl.getUniformLocation(program, 'uFillMode')
    this.showIsolineUniform = gl.getUniformLocation(program, 'uShowIsoline')
    this.setCoordinates(coordinates)
    this.setImageData(imageBody, imageHeight)
    this.setPaletteData(palette)

    this.isDisposed = false
  }

  private setUniforms() {
    const renderingContext = this.renderingContext

    renderingContext.uniform1i(this.imageUniform, 0)
    renderingContext.uniform1i(this.paletteTextureUniform, 1)
    renderingContext.uniform1f(this.nullValueUniform, this.nullValue)

    renderingContext.uniformMatrix3fv(
      this.projectionLocation,
      false,
      getGridCoordinatesToProjectionMatrix(
        this.canvas,
        this.zoom,
        glMatrix.toRadian(this.rotate),
        this.coordinates,
      ),
    )
    renderingContext.uniform1f(this.rangeMinUniform, this.range[0])
    renderingContext.uniform1f(this.rangeMaxUniform, this.range[1])
    renderingContext.uniform1f(this.baseLineUniform, this.baseline)
    renderingContext.uniform1f(this.isolineStepUniform, this.isolineStep)
    renderingContext.uniform1f(this.majorIsolineUniform, this.majorIsoline)
    renderingContext.uniform1f(this.majorIsolineWidthUniform, this.majorIsolineWidth)
    renderingContext.uniform1f(this.minorIsolineWidthUniform, this.minorIsolineWidth)
    renderingContext.uniform1i(this.fillModeUniform, 0)
    renderingContext.uniform1i(this.showIsolineUniform, this.showIsolines)
  }

  public render() {
    if (this.isDisposed) return

    const renderingContext = this.renderingContext

    renderingContext.disable(renderingContext.BLEND)

    renderingContext.activeTexture(renderingContext.TEXTURE0)
    renderingContext.bindTexture(renderingContext.TEXTURE_2D, this.textureBuffer)
    renderingContext.activeTexture(renderingContext.TEXTURE1)
    renderingContext.bindTexture(renderingContext.TEXTURE_2D, this.paletteTextureBuffer)

    bindIndexBuffer(renderingContext, this.indexBuffer)

    bindAttributes(renderingContext, this.attributes)

    renderingContext.useProgram(this.program)

    this.setUniforms()

    renderingContext.drawElements(renderingContext.TRIANGLES, 6, renderingContext.UNSIGNED_SHORT, 0)

    unbindAttributes(renderingContext, this.attributes)

    renderingContext.bindBuffer(renderingContext.ELEMENT_ARRAY_BUFFER, null)
    renderingContext.bindTexture(renderingContext.TEXTURE_2D, null)
  }
  public setZoomState(zoom: ScaleType) {
    this.zoom = zoom
    return this
  }
  public setBaseline(baseline: number) {
    this.baseline = baseline
    return this
  }
  public setIsolineStep(isolineStep: number) {
    this.isolineStep = isolineStep
    return this
  }
  public setMinorIsolineWidth(minorIsolineWidth: number) {
    this.minorIsolineWidth = minorIsolineWidth
    return this
  }
  public setMajorIsolineWidth(majorIsolineWidth: number) {
    this.majorIsolineWidth = majorIsolineWidth
    return this
  }
  public setMajorIsoline(majorIsoline: number) {
    this.majorIsoline = majorIsoline
    return this
  }
  public setRange([rangeMin, rangeMax]: MinMax) {
    this.range = [rangeMin, rangeMax]
    return this
  }
  public setCoordinates(coordinates: Range2D) {
    this.coordinates = coordinates

    const x1 = 0
    const x2 = this.coordinates.width
    const y1 = -this.coordinates.height
    const y2 = 0

    updateAttributeBuffer(
      this.renderingContext,
      this.attributes['aPosition'],
      new Float32Array([x1, y1, x1, y2, x2, y2, x2, y1]),
    )

    updateAttributeBuffer(
      this.renderingContext,
      this.attributes['aTextureCoord'],
      new Float32Array([0, 1, 0, 0, 1, 0, 1, 1]),
    )

    const indices = [0, 1, 2, 2, 3, 0]
    this.renderingContext.deleteBuffer(this.indexBuffer)
    this.indexBuffer = createIndexBufferUint16(this.renderingContext, indices)
    return this
  }
  public setImageData(imageBody: GridType['body'], imageHeight: number) {
    this.imageWidth = Math.ceil(imageBody.length / imageHeight)
    this.imageHeight = imageHeight

    this.textureBuffer = createTextureBuffer(
      this.renderingContext,
      this.imageWidth,
      imageHeight,
      imageBody,
    )
    return this
  }
  public setPaletteData(palette: PaletteBody) {
    this.paletteTextureBuffer = createPaletteTexture(this.renderingContext, palette)
    return this
  }
  public setShowIsolines(showIsolines: boolean) {
    this.showIsolines = booleanToNumber(showIsolines)
    return this
  }

  [Symbol.dispose](): void {
    const renderingContext = this.renderingContext
    renderingContext.deleteTexture(this.textureBuffer)
    renderingContext.deleteTexture(this.paletteTextureBuffer)
    renderingContext.bindBuffer(renderingContext.ARRAY_BUFFER, null)
    deleteAttributes(renderingContext, this.attributes)
    renderingContext.deleteBuffer(this.indexBuffer)
    this.isDisposed = true
  }
}
